import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { activitiesPostBookingApi } from "@b2bportal/core-activities";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { ProductType } from "@b2bportal/core-types";
import {
  ActivitiesCancelScenarioEnum,
  type ActivitiesCancelScenario,
  type ActivityReservation,
} from "@b2bportal/rbc-activities-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  Button,
  ButtonTypeEnum,
  Card,
  Icon,
  PageLoadingScreen,
} from "@tenant/ui";
import DOMPurify from "dompurify";
import styles from "./ActivityReservationCancellation.module.scss";
import { ContactSupportModal } from "@/components";
import {
  ActivityCancelStep,
  useActivityCancelPolicy,
  useActivityCancelPolicyBreakdown,
} from "@/modules/activities-postbooking/hooks";
import { ActivityDetailsPanel } from "@/overrides/activities/ActivityReservationPage/components/ActivityDetailsPanel";
import {
  CancelImportantInfo,
  CancellationError,
  CancellationSuccess,
  RefundBreakdown,
} from "@/pages/Trips/shared/components";

const COMPONENT_KEY = "ActivityReservationCancellation";

interface CancellationContentProps {
  step: ActivityCancelStep;
  scenario: ActivitiesCancelScenario | null;
  reservation: ActivityReservation;
  onBack: () => void;
  getPolicyInfo: () => Promise<void>;
  cn: (name: string) => string;
  renderScenarioCard: (
    scenario: ActivitiesCancelScenario,
    options: { showConfirm: boolean }
  ) => JSX.Element;
  renderDisclaimer: (disclaimer: string) => JSX.Element;
  breakdown: {
    productType: ProductType.ACTIVITIES;
    data: { items: Array<{ label: string; value: string }> };
  } | null;
}

const CancellationContent = ({
  step,
  scenario,
  reservation,
  onBack,
  getPolicyInfo,
  cn,
  renderScenarioCard,
  renderDisclaimer,
  breakdown,
}: CancellationContentProps): JSX.Element | null => {
  const { t } = useI18nContext();

  switch (step) {
    case ActivityCancelStep.LoadingOrProcessing:
      return (
        <PageLoadingScreen
          isLoading
          text={t("selfServe.cancelLoadingTitle")}
          subtitle={t("selfServe.processingSubtitle")}
        />
      );
    case ActivityCancelStep.Fulfilling:
      return (
        <PageLoadingScreen
          isLoading
          text={t("selfServe.cancelFulfillTitle")}
          subtitle={t("selfServe.cancelFulfillSubtitle")}
        />
      );
    case ActivityCancelStep.Complete:
      return (
        <CancellationSuccess
          bookingEmail={
            reservation.bookingDetails?.bookingCommunicationInfo?.email
          }
          breakdown={breakdown}
        />
      );
    case ActivityCancelStep.Error:
      return <CancellationError onRetry={getPolicyInfo} />;
    case ActivityCancelStep.PolicyInfo:
      if (scenario) {
        return renderScenarioCard(scenario, { showConfirm: true });
      }
      return null;
    case ActivityCancelStep.ContactSupport:
      if (
        scenario &&
        scenario.ActivitiesCancelScenario ===
          ActivitiesCancelScenarioEnum.ContactCustomerService
      ) {
        return (
          <Card className={cn("policy-card")}>
            <h1 className={cn("policy-title")}>
              {scenario.cancelCopy.title || t("selfServe.supportTitle")}
            </h1>
            {scenario.cancelCopy.body?.map((bodyText, index) => (
              <p
                key={index}
                className={cn("policy-body")}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(bodyText),
                }}
              />
            ))}
            {scenario.cancelCopy.disclaimer &&
              renderDisclaimer(scenario.cancelCopy.disclaimer)}
            <p className={cn("policy-body")}>{scenario.phoneNumber}</p>
            <ContactSupportModal
              trigger={
                <Button
                  buttonType={ButtonTypeEnum.Primary}
                  className={cn("confirm-btn")}
                >
                  {t("selfServe.contactSupport")}
                </Button>
              }
            />
            <Button
              buttonType={ButtonTypeEnum.Link}
              className={cn("back-link")}
              onClick={onBack}
            >
              {t("selfServe.backToTrips")}
            </Button>
          </Card>
        );
      }
      return null;
    case ActivityCancelStep.BookingPending:
    case ActivityCancelStep.CancellationPending:
    case ActivityCancelStep.TerminalInfo:
      if (scenario) {
        return renderScenarioCard(scenario, { showConfirm: false });
      }
      return null;
    default:
      return <CancellationError onRetry={getPolicyInfo} />;
  }
};

export interface ActivityReservationCancellationProps {
  reservation: ActivityReservation;
  onBack: () => void;
}

export const ActivityReservationCancellation = ({
  reservation,
  onBack,
}: ActivityReservationCancellationProps) => {
  const { t } = useI18nContext();
  const dispatch = useDispatch();
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const onFulfilled = () => {
    void dispatch(
      activitiesPostBookingApi.util.invalidateTags(["ActivityReservationList"])
    );
  };

  const { step, scenario, getPolicyInfo, confirmCancellation } =
    useActivityCancelPolicy({ reservation, onFulfilled });

  const { breakdown } = useActivityCancelPolicyBreakdown(scenario, reservation);

  const activityBreakdown = breakdown
    ? ({ productType: ProductType.ACTIVITIES, data: breakdown } as const)
    : null;

  useEffect(() => {
    void getPolicyInfo();
  }, [getPolicyInfo]);

  const renderDisclaimer = (disclaimer: string) => (
    <div className={cn("policy-disclaimer")}>
      <Icon
        iconName={IconNameEnum.info}
        className={cn("policy-disclaimer-icon")}
      />
      <p className={cn("policy-disclaimer-text")}>{disclaimer}</p>
    </div>
  );

  const renderScenarioCard = (
    cancellationScenario: ActivitiesCancelScenario,
    options: { showConfirm: boolean }
  ): JSX.Element => {
    const copy = cancellationScenario.cancelCopy;
    const confirmationCopy =
      "cancelConfirmationCopy" in cancellationScenario
        ? cancellationScenario.cancelConfirmationCopy
        : undefined;

    return (
      <Card className={cn("policy-card")}>
        <h1 className={cn("policy-title")}>
          {copy.title || t("selfServe.yourCancellation")}
        </h1>
        {copy.body?.map((bodyText, index) => (
          <p
            key={index}
            className={cn("policy-body")}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(bodyText),
            }}
          />
        ))}
        {copy.disclaimer ? renderDisclaimer(copy.disclaimer) : null}
        {activityBreakdown && <RefundBreakdown breakdown={activityBreakdown} />}
        <CancelImportantInfo items={copy.importantInfo} />
        {confirmationCopy != null && options.showConfirm ? (
          <>
            <hr className={cn("policy-divider")} />
            <h2 className={cn("policy-subtitle")}>{confirmationCopy.title}</h2>
            {confirmationCopy.body?.map((bodyText, index) => (
              <p
                key={index}
                className={cn("policy-body")}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(bodyText),
                }}
              />
            ))}
          </>
        ) : null}
        {options.showConfirm && (
          <Button
            buttonType={ButtonTypeEnum.Primary}
            className={cn("confirm-btn")}
            onClick={confirmCancellation}
          >
            {t("cancelFlightModal.confirmBtn")}
          </Button>
        )}
        <Button
          buttonType={ButtonTypeEnum.Link}
          className={cn("back-link")}
          onClick={onBack}
        >
          {t("selfServe.backToTrips")}
        </Button>
      </Card>
    );
  };

  const showRightColumn =
    step !== ActivityCancelStep.LoadingOrProcessing &&
    step !== ActivityCancelStep.Fulfilling;

  return (
    <div className={block}>
      <div
        className={cn("two-column", {
          singleCol: !showRightColumn,
        })}
      >
        <div className={cn("left-column")}>
          <CancellationContent
            step={step}
            scenario={scenario}
            reservation={reservation}
            onBack={onBack}
            getPolicyInfo={getPolicyInfo}
            cn={cn}
            renderScenarioCard={renderScenarioCard}
            renderDisclaimer={renderDisclaimer}
            breakdown={activityBreakdown}
          />
        </div>
        {showRightColumn && (
          <div className={cn("right-column")}>
            <ActivityDetailsPanel reservation={reservation} />
          </div>
        )}
      </div>
    </div>
  );
};
